import { Contact, LinkedAccount, Person } from 'dyl-components';
import { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Dropdown, Grid, Icon } from 'semantic-ui-react';
import { ContactContext } from 'shared/context/ContactProvider';
import SendEmailModal from 'shared/modals/SendEmailModal';
import { NotesModal, TextMessageModal } from 'shared/modals';
import { PhoneUtil, StringUtils } from 'utils';
import AccountContact from './AccountContact';
import InfluenceField from './InfluenceField';
import { ActivityPopup } from 'shared/forms/NewActivity';
import TextIcon from 'shared/TextIcon';
import RestrictedView from 'shared/RestrictedView';
import { USER_PERMISSIONS } from 'dyl-components';

export const AccountContacts = ({
    onPin,
    onUnpin,
    onReplacePinnedContact,

    onSetPrimary,

    isSidePanelOpen,
}) => {
    const dispatch = useDispatch();
    const { account, contactsForPinning, isReadingContactsForPinning, userPhoneCreds, hasEmailIntegrations } = useSelector(state => ({
        account: state.account.account,

        contactsForPinning: state.account.contactsForPinning.map(contact => {
            const name = `${contact.first_name} ${contact.last_name}`;

            return ({
                key: contact.id,
                value: contact.id,
                text: name,
                content: (
                    <Person username={name} subtitleLabel='Job Title' email={contact?.job_title || ''} />
                )
            });
        }),
        isReadingContactsForPinning: state.account.isReadingContactsForPinning,
        userPhoneCreds: state?.officeView?.creds,
        hasEmailIntegrations: state.oauth_integrations.integrations?.length > 0
    }));

    const isPhoneSystemSupported =
        PhoneUtil.isPhoneSystemSupported(userPhoneCreds);

    const contactColumnWidth = 9;

    const primary_contact = account.primary_contact;
    const pinnedContacts = account.pinned;

    const { account_id } = useParams();

    const isPrimary = (contact_id) => {
        return primary_contact.id === contact_id
    };

    const isPinned = (contact_id) => {
        return (pinnedContacts.findIndex(contact => contact.id === contact_id) !== -1)
    }

    const pinOptions = contactsForPinning.filter(({ value }) => !isPrimary(value) && !isPinned(value));

    const [contactBeingEdited, setContactBeingEdited] = useState(null);

    const onEdit = (id) => {
        setContactBeingEdited(id);
    }

    const onCancelEdit = () => {
        setContactBeingEdited(null);
    }

    const onClickToDial = (contact) => {
        const { id: account_id } = account || {};
        const { id: contact_id } = contact || {};
        const { phone: destination } = contact?.primary_phone || {}
        PhoneUtil.C2D({ payload: { contact_id, account_id, destination }, dispatch });
    }

    const rows = [
        ...pinnedContacts.map(contact => {
            return (
                <AccountContact
                    contact={contact}
                    contactBeingEdited={contactBeingEdited}
                    account_id={account_id}
                    onCancelEdit={onCancelEdit}
                    contactColumnWidth={contactColumnWidth}
                    onUnpin={onUnpin}
                    isSidePanelOpen={isSidePanelOpen}
                    pinOptions={pinOptions}
                    onReplacePinnedContact={onReplacePinnedContact}
                    isReadingContactsForPinning={isReadingContactsForPinning}
                    onEdit={onEdit}
                    onClickToDial={onClickToDial}
                    onText={() => {onOpenModal('text', contact.id)}}
                    assignees={account?.assignees}
                />
            );
        })
    ]

    while (rows.length < 3) {
        rows.push((
            <Grid.Row verticalAlign='middle'>
                <Grid.Column width={contactColumnWidth}>
                    <div className='AccountPage__ContactRow'>
                        <Icon color='grey' className='fas thumbtack' />
                        <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(account?.assignees?.owner_id || 0), (account?.assignees?.coowner_id || 0)]}>
                            <Dropdown
                                placeholder='Pin a Contact'
                                selectOnBlur={false}
                                selection
                                options={pinOptions}
                                onChange={(_, { value: contact_id }) => { onPin(contact_id) }}
                                compact
                                value={null}
                                text={'Pin a Contact'}
                                style={{ width: '15em' }}
                                loading={isReadingContactsForPinning}
                            />
                        </RestrictedView>
                    </div>
                </Grid.Column>
            </Grid.Row>
        ))
    }

    const { config: { modalType, contact }, onOpenModal, onCloseModal } = useContext(ContactContext);
    const contactToBeTexted = contactsForPinning.find(option => option.value === contact);
    return (
        <>
            <Grid>
                <Grid.Row columns='equal'>
                    <Grid.Column width={contactColumnWidth}>
                        <strong>Primary Contact</strong>
                    </Grid.Column>
                    <Grid.Column>
                        <strong>Role</strong>
                    </Grid.Column>
                    <Grid.Column>
                        <strong>Module/Pipeline</strong>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row verticalAlign='middle' columns='equal'>
                    <Grid.Column width={contactColumnWidth}>
                        <div className='AccountPage__ContactRow'>
                            <Icon color={primary_contact?.id ? 'primary' : 'grey'} className='fas fa-address-book' />
                            {primary_contact?.id ? (
                                <>
                                    <Contact
                                        leftSideContent={
                                            <>
                                                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.ACCOUNT_EDIT} userRestrict assignmentIds={[(account?.assignees?.owner_id || 0), (account?.assignees?.coowner_id || 0)]}>
                                                    <Dropdown
                                                        placeholder='Select Primary'
                                                        selectOnBlur={false}
                                                        selection
                                                        options={contactsForPinning.filter(({ value }) => value !== primary_contact.id)}
                                                        value={primary_contact.id}
                                                        onChange={(_, { value: contact_id }) => {
                                                            onSetPrimary(contact_id, true)
                                                        }}
                                                        compact
                                                        text={(
                                                            <LinkedAccount
                                                                account={`${primary_contact.first_name} ${primary_contact.last_name}`}
                                                                maxWidth={'8.25em'}
                                                                subtitle={primary_contact?.job_title}
                                                                popup
                                                                linkToAccount={
                                                                    <RestrictedView type="link" userPermission={USER_PERMISSIONS.CONTACTS_VIEW} userRestrict assignmentIds={[(primary_contact?.owner_id || 0), (primary_contact?.coowner_id || 0)]}>
                                                                        <Link onClick={e => { e.stopPropagation(); }} target={'_blank'} to={`/contact/${primary_contact.id}`}>{primary_contact.first_name} {primary_contact.last_name}</Link>
                                                                    </RestrictedView>
                                                                }
                                                            />
                                                        )}
                                                        style={{ width: '15em' }}
                                                        loading={isReadingContactsForPinning}
                                                    />
                                                </RestrictedView>
                                            </>
                                        }

                                        phone={primary_contact?.primary_phone?.phone}
                                        email={primary_contact?.primary_email?.email}
                                        account_id={account_id}
                                        contact_id={primary_contact?.id}
                                        isPhoneSystemSupported={isPhoneSystemSupported}
                                        hasEmailIntegrations={hasEmailIntegrations}
                                        onCallClick={() => onClickToDial(primary_contact)}
                                        onTextClick={() => onOpenModal('text', primary_contact.id)}
                                        onEmailClick={() => onOpenModal('email', primary_contact.id)}
                                        onNoteClick={() => onOpenModal('note', primary_contact)}
                                        ActivityPopup={ActivityPopup}
                                        TextIcon={TextIcon}
                                        callActivityProps={{
                                            hideActivitySelection: true,
                                            contact: { id: primary_contact?.id },
                                            isContact: true
                                        }}
                                        emailActivityProps={{
                                            readTimeline: () => { },
                                            contact,
                                            isContact: true,
                                            emailOnly: true
                                        }}
                                        isFixedWidth={false}
                                    />

                                </>
                            ) : (
                                <Dropdown
                                    placeholder='Select Primary'
                                    selectOnBlur={false}
                                    selection
                                    options={contactsForPinning}
                                    value={null}
                                    onChange={(_, { value: contact_id }) => {
                                        onSetPrimary(contact_id);
                                    }}
                                    compact
                                    clearable
                                    style={{ width: '15em' }}
                                    text='Select Primary'
                                    loading={isReadingContactsForPinning}
                                />
                            )}
                        </div>
                    </Grid.Column>
                    <Grid.Column>
                        {primary_contact?.id && (
                            <InfluenceField
                                account_id={account_id}
                                contactBeingEdited={contactBeingEdited}
                                id={primary_contact?.id}
                                influence={primary_contact?.influence}
                                onCancelEdit={onCancelEdit}
                                onEdit={onEdit}
                                assignees={account?.assignees}
                            />
                        )}
                    </Grid.Column>
                    <Grid.Column>
                        {Object.keys(primary_contact).length > 1 ? primary_contact?.module_summary ?
                            `${StringUtils.capitalize(primary_contact.module_summary.account_stage)}-${primary_contact.module_summary.pipeline_stage_name}` : ''
                            : ''}
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column>
                        <strong>Other Contacts</strong>
                    </Grid.Column>
                </Grid.Row>
                {rows}
            </Grid>
            <SendEmailModal
                open={modalType === "email"}
                onClose={() => { onCloseModal() }}
                contact_id={contact}
            />
            <NotesModal
                open={modalType === "note"}
                onClose={() => { onCloseModal() }}
                contact={contact}
            />
            <TextMessageModal
                open={modalType === "text"}
                onClose={() => { onCloseModal() }}
                contact_name={contactToBeTexted?.text || ''}
                contact_id={contact}
            />
        </>
    )
}
